export type ClientRealtimeTraceStage =
  | 'client_input_sent'
  | 'client_snapshot_received'
  | 'client_snapshot_applied'
  | 'client_reconcile'
  | 'client_remote_visual_frame'
  | 'client_slippery_camera_frame';

export interface ClientRealtimeTraceEvent {
  readonly stage: ClientRealtimeTraceStage;
  readonly minigameId: string;
  readonly instanceId: string;
  readonly playerId?: string;
  readonly roundIndex?: number;
  readonly inputSeq?: number;
  readonly clientTick?: number;
  readonly sequence?: number;
  readonly serverTick?: number;
  readonly serverNowMs?: number;
  readonly snapshotAgeMs?: number;
  readonly correctionDistance?: number;
  readonly postReconcileDistance?: number;
  readonly postReplayDistance?: number;
  readonly snapped?: boolean;
  readonly remotePlayerCount?: number;
  readonly detail?: string;
  readonly payload?: unknown;
}

export interface ClientRealtimeTraceEntry extends ClientRealtimeTraceEvent {
  readonly side: 'client';
  readonly atIso: string;
  readonly atMs: number;
  readonly perfNowMs: number;
}

export interface ClientRealtimeTraceSummary {
  readonly entries: number;
  readonly inputs: number;
  readonly snapshots: number;
  readonly corrections: number;
  readonly remoteVisualFrames: number;
  readonly maxSnapshotAgeMs: number | null;
  readonly avgSnapshotAgeMs: number | null;
  readonly maxCorrectionDistance: number | null;
  readonly avgCorrectionDistance: number | null;
  readonly maxRemoteVisualDelta: number | null;
  readonly p95RemoteVisualDelta: number | null;
  readonly maxRemoteMeshDelta: number | null;
  readonly p95RemoteMeshDelta: number | null;
  readonly maxRemoteFrameDeltaMs: number | null;
  readonly p95RemoteFrameDeltaMs: number | null;
  readonly snapshotReceiveIntervalMs: ClientRealtimeTraceIntervalSummary;
  readonly snapshotServerIntervalMs: ClientRealtimeTraceIntervalSummary;
  readonly snapshotBurstCount: number;
  readonly snapshotLateCount: number;
  readonly snapCount: number;
}

export interface ClientRealtimeTraceIntervalSummary {
  readonly count: number;
  readonly median: number | null;
  readonly p90: number | null;
  readonly p95: number | null;
  readonly p99: number | null;
  readonly max: number | null;
}

interface RealtimeTraceApi {
  start(): void;
  stop(): void;
  clear(): void;
  get(): readonly ClientRealtimeTraceEntry[];
  summary(): ClientRealtimeTraceSummary;
  download(filename?: string): void;
}

declare global {
  interface Window {
    smashpartyRealtimeTrace?: RealtimeTraceApi;
  }
}

const ENABLED_STORAGE_KEY = 'smashparty_realtime_trace_enabled';
const CONSOLE_STORAGE_KEY = 'smashparty_realtime_trace_console';
const MAX_TRACE_ENTRIES = 5_000;
const MAX_PAYLOAD_DEPTH = 5;

let traceEntries: ClientRealtimeTraceEntry[] = [];
let initialized = false;

export function installClientRealtimeTraceControls(): void {
  if (typeof window === 'undefined' || initialized) return;
  initialized = true;
  window.smashpartyRealtimeTrace = {
    start: () => setClientRealtimeTraceEnabled(true),
    stop: () => setClientRealtimeTraceEnabled(false),
    clear: clearClientRealtimeTrace,
    get: getClientRealtimeTrace,
    summary: summarizeClientRealtimeTrace,
    download: downloadClientRealtimeTrace,
  };
}

export function setClientRealtimeTraceEnabled(enabled: boolean): void {
  try {
    if (typeof localStorage !== 'undefined') {
      if (enabled) localStorage.setItem(ENABLED_STORAGE_KEY, '1');
      else localStorage.removeItem(ENABLED_STORAGE_KEY);
    }
  } catch {
    // Storage access is best-effort in private/test browser contexts.
  }
}

export function isClientRealtimeTraceEnabled(): boolean {
  const globalFlag = (globalThis as { __SMASHPARTY_REALTIME_TRACE__?: unknown })
    .__SMASHPARTY_REALTIME_TRACE__;
  if (isTruthyFlag(globalFlag)) return true;
  try {
    if (
      typeof localStorage !== 'undefined' &&
      isTruthyFlag(localStorage.getItem(ENABLED_STORAGE_KEY))
    ) {
      return true;
    }
  } catch {
    // Ignore storage access failures.
  }
  try {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return isTruthyFlag(params.get('realtimeTrace')) || isTruthyFlag(params.get('rtTrace'));
  } catch {
    return false;
  }
}

export function isClientRealtimeTraceFullEnabled(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('realtimeTrace') === 'full' || params.get('rtTrace') === 'full';
  } catch {
    return false;
  }
}

function isClientRealtimeTraceConsoleEnabled(): boolean {
  try {
    if (
      typeof localStorage !== 'undefined' &&
      isTruthyFlag(localStorage.getItem(CONSOLE_STORAGE_KEY))
    ) {
      return true;
    }
  } catch {
    // Ignore storage access failures.
  }
  try {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return (
      isTruthyFlag(params.get('realtimeTraceConsole')) || isTruthyFlag(params.get('rtTraceConsole'))
    );
  } catch {
    return false;
  }
}

export function logClientRealtimeTrace(
  event: ClientRealtimeTraceEvent,
): ClientRealtimeTraceEntry | null {
  if (!isClientRealtimeTraceEnabled()) return null;
  const entry: ClientRealtimeTraceEntry = {
    side: 'client',
    atIso: new Date().toISOString(),
    atMs: Date.now(),
    perfNowMs: nowPerfMs(),
    ...event,
    ...(event.payload === undefined ? {} : { payload: sanitizeTracePayload(event.payload) }),
  };
  traceEntries = [...traceEntries, entry].slice(-MAX_TRACE_ENTRIES);
  if (isClientRealtimeTraceConsoleEnabled()) {
    // eslint-disable-next-line no-console
    console.debug('[realtime-trace]', entry);
  }
  return entry;
}

export function getClientRealtimeTrace(): readonly ClientRealtimeTraceEntry[] {
  return traceEntries;
}

export function clearClientRealtimeTrace(): void {
  traceEntries = [];
}

export function summarizeClientRealtimeTrace(): ClientRealtimeTraceSummary {
  const entries = getClientRealtimeTrace();
  const snapshotAges = entries
    .map((entry) => entry.snapshotAgeMs)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  const corrections = entries
    .map((entry) => entry.correctionDistance)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  const remoteVisualFrames = entries.filter(
    (entry) => entry.stage === 'client_remote_visual_frame',
  );
  const remoteVisualDeltas = remoteVisualFrames
    .map((entry) => extractPayloadNumber(entry.payload, 'visualDelta'))
    .filter((value): value is number => value !== null);
  const remoteMeshDeltas = remoteVisualFrames
    .map((entry) => extractPayloadNumber(entry.payload, 'meshDelta'))
    .filter((value): value is number => value !== null);
  const remoteFrameDeltas = remoteVisualFrames
    .map((entry) => extractPayloadNumber(entry.payload, 'frameDeltaMs'))
    .filter((value): value is number => value !== null);
  const snapshotIntervals = consecutiveSnapshotIntervals(entries);
  return {
    entries: entries.length,
    inputs: entries.filter((entry) => entry.stage === 'client_input_sent').length,
    snapshots: entries.filter((entry) => entry.stage === 'client_snapshot_received').length,
    corrections: corrections.length,
    remoteVisualFrames: remoteVisualFrames.length,
    maxSnapshotAgeMs: maxOrNull(snapshotAges),
    avgSnapshotAgeMs: averageOrNull(snapshotAges),
    maxCorrectionDistance: maxOrNull(corrections),
    avgCorrectionDistance: averageOrNull(corrections),
    maxRemoteVisualDelta: maxOrNull(remoteVisualDeltas),
    p95RemoteVisualDelta: percentileOrNull(remoteVisualDeltas, 95),
    maxRemoteMeshDelta: maxOrNull(remoteMeshDeltas),
    p95RemoteMeshDelta: percentileOrNull(remoteMeshDeltas, 95),
    maxRemoteFrameDeltaMs: maxOrNull(remoteFrameDeltas),
    p95RemoteFrameDeltaMs: percentileOrNull(remoteFrameDeltas, 95),
    snapshotReceiveIntervalMs: intervalSummary(
      snapshotIntervals
        .map((interval) => interval.receiveIntervalMs)
        .filter((value): value is number => value !== null),
    ),
    snapshotServerIntervalMs: intervalSummary(
      snapshotIntervals
        .map((interval) => interval.serverIntervalMs)
        .filter((value): value is number => value !== null),
    ),
    snapshotBurstCount: snapshotIntervals.filter(
      (interval) => interval.receiveIntervalMs !== null && interval.receiveIntervalMs < 25,
    ).length,
    snapshotLateCount: snapshotIntervals.filter(
      (interval) => interval.receiveIntervalMs !== null && interval.receiveIntervalMs > 85,
    ).length,
    snapCount: entries.filter((entry) => entry.snapped === true).length,
  };
}

function consecutiveSnapshotIntervals(entries: readonly ClientRealtimeTraceEntry[]): ReadonlyArray<{
  readonly receiveIntervalMs: number | null;
  readonly serverIntervalMs: number | null;
}> {
  const snapshots = entries
    .filter((entry) => entry.stage === 'client_snapshot_received')
    .filter((entry) => typeof entry.sequence === 'number')
    .sort((a, b) => a.perfNowMs - b.perfNowMs);
  const intervals: Array<{
    readonly receiveIntervalMs: number | null;
    readonly serverIntervalMs: number | null;
  }> = [];
  const previousByInstance = new Map<string, ClientRealtimeTraceEntry>();
  for (const snapshot of snapshots) {
    const key = `${snapshot.minigameId}:${snapshot.instanceId}`;
    const previous = previousByInstance.get(key);
    if (
      previous !== undefined &&
      previous.sequence !== undefined &&
      snapshot.sequence === previous.sequence + 1
    ) {
      intervals.push({
        receiveIntervalMs: finiteDelta(snapshot.perfNowMs, previous.perfNowMs),
        serverIntervalMs:
          snapshot.serverNowMs === undefined || previous.serverNowMs === undefined
            ? null
            : finiteDelta(snapshot.serverNowMs, previous.serverNowMs),
      });
    }
    previousByInstance.set(key, snapshot);
  }
  return intervals;
}

function downloadClientRealtimeTrace(filename = 'smashparty-realtime-trace.json'): void {
  if (typeof document === 'undefined') return;
  const blob = new Blob([JSON.stringify(getClientRealtimeTrace(), null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function sanitizeTracePayload(value: unknown, depth = 0, seen = new WeakSet<object>()): unknown {
  if (depth > MAX_PAYLOAD_DEPTH) return '[MaxDepth]';
  if (typeof value !== 'object' || value === null) return value;
  if (seen.has(value)) return '[Circular]';
  seen.add(value);
  if (Array.isArray(value)) {
    return value.slice(0, 32).map((item) => sanitizeTracePayload(item, depth + 1, seen));
  }
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value).slice(0, 64)) {
    out[key] = sanitizeTracePayload(item, depth + 1, seen);
  }
  return out;
}

function isTruthyFlag(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value !== 'string') return false;
  return ['1', 'true', 'yes', 'on', 'full'].includes(value.toLowerCase());
}

function nowPerfMs(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

function averageOrNull(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function maxOrNull(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  return Math.max(...values);
}

function percentileOrNull(values: readonly number[], percentile: number): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.min(
    sorted.length - 1,
    Math.max(0, Math.ceil((percentile / 100) * sorted.length) - 1),
  );
  return sorted[index] ?? null;
}

function intervalSummary(values: readonly number[]): ClientRealtimeTraceIntervalSummary {
  const sorted = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  return {
    count: sorted.length,
    median: percentileOrNull(sorted, 50),
    p90: percentileOrNull(sorted, 90),
    p95: percentileOrNull(sorted, 95),
    p99: percentileOrNull(sorted, 99),
    max: maxOrNull(sorted),
  };
}

function extractPayloadNumber(payload: unknown, key: string): number | null {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null;
  const value = (payload as Record<string, unknown>)[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function finiteDelta(next: number, previous: number): number | null {
  const delta = next - previous;
  return Number.isFinite(delta) && delta >= 0 ? delta : null;
}
